import React, { useState, useEffect } from 'react';
import { SegmentedControl, Container, Title, Group, LoadingOverlay, Text, Center } from '@mantine/core';
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from 'recharts';

const ApiUsageChart = ({ userId }) => {
    const [period, setPeriod] = useState(7);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const barRadius = period <= 7 ? 6 : 4;

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true); // Start loading
            try {
                const response = await fetch('/.netlify/functions/getRecentUsage', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        userId,
                        days: period
                    })
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const result = await response.json();

                const filledResults = [];
                const startDate = new Date();
                startDate.setDate(startDate.getDate() - period + 1); // Set to the start of the period

                for (let day = 0; day < period; day++) {
                    const date = new Date(startDate);
                    date.setDate(date.getDate() + day);
                    const formattedDate = new Date(date).toLocaleString('en-us', {day: '2-digit', month: 'short'});

                    const found = result.find(entry => entry.date === formattedDate);
                    if (found) {
                        filledResults.push(found);
                    } else {
                        filledResults.push({ date: formattedDate, "API Calls": 0 });
                    }
                }

                setData(filledResults);
            } catch (error) {
                console.error('Failed to fetch data:', error);
            } finally {
                setLoading(false); // End loading regardless of success or error
            }
        };

        fetchData();
    }, [userId, period]);

    return (
        <Container sx = {{position: 'relative'}}>
            <LoadingOverlay visible={loading} />
            <Group position="apart" >
                <Text size='lg'>Recent API Usage</Text>
                <SegmentedControl
                    value={period.toString()}
                    onChange={value => setPeriod(Number(value))}
                    data={[
                        { label: 'Last 7 days', value: '7' },
                        { label: 'Last 30 days', value: '30' }
                    ]}
                    radius='md'
                />
            </Group>
            {
                !loading && data.length === 0 ? 
                <Center mt="xl">
                    <Text>Sorry there is no usage data to display. If this is an error please contact us.</Text>
                </Center>
                : data.length !== 0 &&
                <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={data} margin={{top: 25, right: 25, bottom: 5, left: -10}}>
                        <CartesianGrid stroke="#eee" strokeDasharray="5 5"/>
                        <XAxis
                            dataKey="date"
                            style={{fontSize: '12px' }}
                            interval={ period <= 10 ? 1 : 3 }
                            tickMargin = {4}
                        />
                        <YAxis
                            style={{ fontSize: '12px' }}
                            tickMargin = {4}
                        />
                        <Tooltip 
                            contentStyle={{
                                borderRadius: "8px",
                                border: '1px solid #dee2e6',
                            }}
                            cursor = {{fill: 'rgba(0, 0, 0, 0.1)'}}
                        />
                        <Bar dataKey={"API Calls"} fill="#E15759" radius={[barRadius, barRadius, 0, 0]}/>
                    </BarChart>
                </ResponsiveContainer>
            }
        </Container>
    );
};

export default ApiUsageChart;
